/**
 * Shared motion / accessibility helpers.
 *
 * CLAUDE.md performance rules:
 *  - Honour `prefers-reduced-motion`
 *  - Reduce particle count by ~60% on mobile (density scale 0.4)
 *
 * Kept framework-agnostic so both the raw Three.js ParticleEngine and the
 * GSAP-driven React components can share one source of truth.
 */

/** True when the user has asked the OS to minimise motion. SSR-safe. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Coarse pointer / small viewport ⇒ treat as mobile. */
export function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false
  const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false
  return coarse || window.innerWidth < 768
}

/**
 * Multiplier applied to mood particle counts.
 *  - reduced motion ⇒ 0.25 (sparse, near-static field)
 *  - mobile         ⇒ 0.4  (CLAUDE.md: -60%)
 *  - desktop        ⇒ 1
 */
export function particleDensityScale(): number {
  if (prefersReducedMotion()) return 0.25
  if (isMobileViewport()) return 0.4
  return 1
}

/** Subscribe to reduced-motion changes; returns an unsubscribe fn. */
export function onReducedMotionChange(cb: (reduced: boolean) => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {}
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
  const handler = (e: MediaQueryListEvent) => cb(e.matches)
  mq.addEventListener('change', handler)
  return () => mq.removeEventListener('change', handler)
}
